import {
	Vue, Component, Ref, toNative,
} from 'vue-facing-decorator';
import * as DB from 'interfaces/database';
import { BModal } from 'bootstrap-vue';
import Swal from 'sweetalert2';
import mitt from 'mitt';
import { TabEvent } from 'interfaces/app';
import { CellComponentExtended, ColumnDefinitionExtended, TabulatorFull as Tabulator } from 'tabulator-tables';
import { createInstance } from 'utils/vue';
import TabulatorBtn from 'components/Tabulator/TabulatorBtn';
import AddressService from 'services/AddressService';
import Template from './template.vue';

const eventBus = mitt<TabEvent<DB.AddressModel>>();
@Component({
	components: {
		TabulatorBtn,
	},
	emits: ['routePickup'],
	mixins: [Template],
})
class PickupStoresTable extends Vue {
	@Ref('addpickupstores-modal')
	private readonly modal!: BModal;

	@Ref('pickupStoreTable')
	private readonly tableReference!: HTMLDivElement;

	private pickupstore : DB.AddressModel[] = [];

	private PickupStoredata = {} as Pick<DB.AddressModel, 'name'>;

	private name = '';

	private table?: Tabulator;

	private isLoaded = false;

	private isLoading = false;

	private rowHeight = 60;

	private columnDefs: ColumnDefinitionExtended[] = [];

	private get loggedIn(): boolean {
		return this.$auth0.isAuthenticated.value;
	}

	protected beforeMount(): void {
		this.columnDefs = [
			{
				field: 'name',
				title: 'Name',
				headerFilter: true,
			},
			{
				title: 'Actions',
				formatter: (cell: CellComponentExtended<DB.AddressModel>) => {
					const instance = createInstance({
						component: TabulatorBtn,
						props: {
							data: cell.getData(),
							buttons: [
								{
									id: 'edit',
									eventName: 'editItem',
									className: 'fa-edit',
								},
								{
									id: 'delete',
									eventName: 'deleteItem',
									className: 'fa-times',
								},
							],
							eventBus,
						},
					});
					return instance;
				},
			},
		] as ColumnDefinitionExtended[];
	}

	protected mounted(): void {
		eventBus.on(
			'deleteItem',
			this.deletePickupStore,
		);
		eventBus.on(
			'editItem',
			this.editPickupStore,
		);
		this.tableInitialization();
		this.table?.on(
			'tableBuilt',
			this.onTableBuilt,
		);
	}

	protected beforeUnmount(): void {
		eventBus.off(
			'deleteItem',
			this.deletePickupStore,
		);
		eventBus.off(
			'editItem',
			this.editPickupStore,
		);
		this.table?.off(
			'tableBuilt',
			this.onTableBuilt,
		);
		this.table?.destroy();
	}

	private onTableBuilt(): void {
		this.getData();
	}

	private tableInitialization(): void {
		this.table = new Tabulator(
			this.tableReference,
			{
				height: '60vh',
				layout: 'fitColumns',
				columns: this.columnDefs,
			},
		);
	}

	private async getData(): Promise<void> {
		const parameter = new URLSearchParams({
			limit: '0',
		});
		this.table?.alert('Loading');
		try {
			const data = await AddressService.getAll(parameter);
			this.table?.setData(data);
		} catch (err: any) {
			this.$toastError(err.message);
		} finally {
			this.table?.clearAlert();
		}
	}

	protected addPickupStore(): void {
		this.isLoaded = true;
		AddressService.create(
			{ ...this.PickupStoredata },
		).then((res) => {
			this.modal.hide();

			this.PickupStoredata = {
				name: '',
			};
			this.$emit(
				'routePickup',
				res.id,
			);
			return undefined;
		})
			.finally(() => {
				this.isLoaded = false;
			})
			.catch((err) => {
				this.$toastError(err.message);
			});
	}

	private editPickupStore(data: TabEvent<DB.AddressModel>['editItem']): void {
		this.$emit(
			'routePickup',
			data.id,
		);
	}

	private deletePickupStore(data: TabEvent<DB.AddressModel>['deleteItem']): void {
		Swal.fire({
			title: 'Are you sure?',
			text: 'You will not be able to recover this file!',
			icon: 'warning',
			showCancelButton: true,
			customClass: {
				confirmButton: 'btn btn-danger m-1',
				cancelButton: 'btn btn-secondary m-1',
			},
			confirmButtonText: 'Yes, delete it!',
			html: false,
		}).then((result) => {
			if (result.value) {
				this.table?.alert('Loading..');
				// eslint-disable-next-line promise/no-nesting
				AddressService.delete(data.id).then(() => {
					this.$toastSuccess('Item Deleted');
					this.table?.deleteRow(data.id);
					return null;
				}).finally(() => {
					this.table?.clearAlert();
				}).catch((err) => {
					this.$toastError(err.message);
				});
			}
			return undefined;
		}).catch((err) => {
			this.$toastError(err.message);
		});
	}
}

export default toNative(PickupStoresTable);
